/**
 * What administrators changed, newest first, fifty at a time (spec §8.26). A secret appears as
 * changed or cleared, never its value — the server does not keep it in the history.
 */
import { useI18n, useT } from "../i18n";
import { Button } from "../ui/Button";
import { useServerHistory, type AdminEvent } from "./api";
import { FIELD, HISTORY, SETTING } from "./labels";

export function HistoryTab() {
  const t = useT();
  const history = useServerHistory();
  const items = history.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <section className="kss-surface p-4" data-testid="server-history-tab">
      <h2 className="text-[17px] font-semibold">{t("server.history")}</h2>
      <p className="mb-3 text-[13px] text-neutral-500">{t("server.history.sub")}</p>
      {history.isError && <p className="text-sm text-neutral-500">{t("server.needServer")}</p>}
      <ol className="divide-y divide-neutral-200 text-sm dark:divide-neutral-800" data-testid="server-history">
        {items.map((e) => (
          <Entry key={e.id} event={e} />
        ))}
        {history.data && items.length === 0 && <li className="py-2 text-neutral-500">{t("server.history.none")}</li>}
      </ol>
      {history.hasNextPage && (
        <div className="mt-3 text-center">
          <Button variant="secondary" size="sm" disabled={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()} data-testid="server-history-more">
            {t("server.history.more")}
          </Button>
        </div>
      )}
    </section>
  );
}

function shown(t: ReturnType<typeof useT>, key: string, v: unknown): string {
  if (key === "KM_LLM_API_KEY") return v ? t("server.secret.set") : t("server.secret.notSet");
  if (v === true) return t("settings.policy.on");
  if (v === false) return t("settings.policy.off");
  if (v === "" || v === null || v === undefined) return t("server.history.empty");
  return String(v);
}

function Entry({ event }: { event: AdminEvent }) {
  const t = useT();
  const { language } = useI18n();
  const who = event.actor ?? t("server.history.command");
  let what: string;
  switch (event.kind) {
    case "setting.saved":
    case "setting.reset":
      what = t(HISTORY[event.kind] ?? event.kind, {
        setting: t(SETTING[event.target] ?? event.target),
        before: shown(t, event.target, event.before),
        after: shown(t, event.target, event.after),
      });
      break;
    case "user.edited":
      what = t("server.history.user.edited", { user: event.target, fields: Object.keys((event.after as object) ?? {}).map((f) => t(FIELD[f] ?? f)).join(", ") });
      break;
    case "user.admin":
    case "user.active":
      what = t(HISTORY[`${event.kind}.${event.after ? "on" : "off"}`] ?? event.kind, { user: event.target });
      break;
    default:
      what = t(HISTORY[event.kind] ?? event.kind, { user: event.target });
  }
  return (
    <li className="flex flex-wrap gap-x-3 py-2" data-testid="server-history-entry" data-kind={event.kind} data-target={event.target}>
      <time className="w-40 shrink-0 tabular-nums text-neutral-500" dateTime={event.at}>
        {new Date(event.at).toLocaleString(language, { dateStyle: "medium", timeStyle: "short" })}
      </time>
      <span className="min-w-0 grow">
        <span className="font-medium">{who}</span> {what}
      </span>
    </li>
  );
}
